import { create } from "zustand"
import { devtools, persist } from "zustand/middleware"
import type { TExercise, TWorkout, TWorkoutProgram, TWorkoutTemplate } from "./utils/workoutUtils"

interface User {
  id: string
  email: string
  name: string | null
}

interface UserState {
  user: User | null
  setUser: (user: User | null) => void
}

export const useUserStore = create<UserState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        setUser: (user) => set({ user: user }),
      }),
      {
        name: "user-storage",
      }
    )
  )
)

type TStats = {
  totalWeightLifted: number
  totalWeightLiftedThisYear: number
}

interface StatsState {
  stats: TStats
  setStats: (stats: TStats) => void
}

export const useStatsStore = create<StatsState>()(
  devtools(
    persist(
      (set) => ({
        stats: { totalWeightLifted: 0, totalWeightLiftedThisYear: 0 },
        setStats: (stats) => set({ stats }),
      }),
      {
        name: "stats-storage",
      }
    )
  )
)

interface ActiveWorkoutState {
  activeWorkout: TWorkout | null
  setActiveWorkout: (workout: TWorkout | null) => void
}

export const useActiveWorkoutStore = create<ActiveWorkoutState>()(
  devtools(
    persist(
      (set) => ({
        activeWorkout: null,
        setActiveWorkout: (workout: TWorkout | null) => set({ activeWorkout: workout }),
      }),
      {
        name: "active-workout-storage",
      }
    )
  )
)

export type TTheme = "light" | "dark" | "system"

export interface ThemeState {
  setTheme: (theme: TTheme) => void
  theme: TTheme
}

export const useThemeStore = create<ThemeState>()(
  devtools(
    persist(
      (set) => ({
        theme: localStorage.theme || "light",
        setTheme: (theme: TTheme) => {
          localStorage.theme = theme
          if (theme === "dark") {
            document.documentElement.classList.add("dark")
          } else {
            document.documentElement.classList.remove("dark")
          }
          document.documentElement.dataset.theme = theme

          return set(() => ({ theme: theme }))
        },
      }),
      {
        name: "theme-storage",
      }
    )
  )
)

interface WorkoutTemplateState {
  workoutTemplate: TWorkoutTemplate | null
  setWorkoutTemplate: (workoutTemplate: TWorkoutTemplate | null) => void
  exercises: Array<TExercise>
  setExercises: (exercises: Array<TExercise>) => void
  color?: string
  setColor: (color: string) => void
}

export const useWorkoutTemplateStore = create<WorkoutTemplateState>()(
  devtools(
    persist(
      (set) => ({
        workoutTemplate: null,
        setWorkoutTemplate: (workoutTemplate: TWorkoutTemplate | null) => set({ workoutTemplate: workoutTemplate }),
        exercises: [],
        setExercises: (exercises: Array<TExercise>) => set({ exercises: exercises }),
        color: "gray",
        setColor: (color: string) => set({ color: color }),
      }),
      {
        name: "workout-template-storage",
      }
    )
  )
)

interface WeightPlateCalculatorState {
  plateInventory: Record<number, number>
  setPlateInventory: (inventory: Record<number, number>) => void
}

export const useWeightPlateInventoryStore = create<WeightPlateCalculatorState>()(
  devtools(
    persist(
      (set) => ({
        plateInventory: {
          45: 0,
          35: 0,
          25: 0,
          15: 0,
          10: 0,
          5: 0,
          2.5: 0,
        },
        setPlateInventory: (inventory: Record<number, number>) => set({ plateInventory: inventory }),
      }),
      {
        name: "weight-plate-inventory-storage",
      }
    )
  )
)

interface ProgramStore {
  programs: TWorkoutProgram[]
  activeProgramId: string | null
  addProgram: (program: TWorkoutProgram) => void
  updateProgram: (programId: string, program: TWorkoutProgram) => void
  deleteProgram: (programId: string) => void
  cloneProgram: (programId: string) => void
  setActiveProgram: (programId: string) => void
  getActiveProgram: () => TWorkoutProgram | null
}

export const useProgramStore = create<ProgramStore>()(
  persist(
    (set, get) => ({
      programs: [],
      activeProgramId: null,

      addProgram: (program) =>
        set((state) => ({
          programs: [
            ...state.programs,
            {
              ...program,
              dateCreated: program.dateCreated || new Date().toISOString(),
              timesCompleted: program.timesCompleted || 0,
              durationWeeks: program.durationWeeks || 8,
            },
          ],
          activeProgramId: program.programId,
        })),

      updateProgram: (programId, updatedProgram) =>
        set((state) => ({
          programs: state.programs.map((p) => (p.programId === programId ? updatedProgram : p)),
        })),

      deleteProgram: (programId) =>
        set((state) => {
          const newPrograms = state.programs.filter((p) => p.programId !== programId)
          return {
            programs: newPrograms,
            activeProgramId:
              state.activeProgramId === programId ? newPrograms[0]?.programId || null : state.activeProgramId,
          }
        }),

      cloneProgram: (programId) => {
        const program = get().programs.find((p) => p.programId === programId)
        if (program) {
          const clonedProgram: TWorkoutProgram = {
            ...program,
            programId: crypto.randomUUID(),
            name: `${program.name} (Copy)`,
            dateCreated: new Date().toISOString(),
            timesCompleted: 0,
            workouts: program.workouts.map((workout) => ({
              ...workout,
              workoutId: crypto.randomUUID(),
              exercises: workout.exercises.map((exercise) => ({
                ...exercise,
                exerciseId: crypto.randomUUID(),
              })),
            })),
          }
          set((state) => ({
            programs: [...state.programs, clonedProgram],
            activeProgramId: clonedProgram.programId,
          }))
        }
      },

      setActiveProgram: (programId) => set({ activeProgramId: programId }),

      getActiveProgram: () => {
        const state = get()
        return state.programs.find((p) => p.programId === state.activeProgramId) || null
      },
    }),
    {
      name: "workout-programs-storage",
    }
  )
)

interface RestTimerState {
  remaining: number
  total: number
  isRunning: boolean
  isPaused: boolean
  showPresets: boolean
  start: (seconds: number) => void
  tick: () => void
  pause: () => void
  resume: () => void
  dismiss: () => void
  reset: () => void
}

export const useRestTimerStore = create<RestTimerState>()(
  devtools(
    (set, get) => ({
      remaining: 0,
      total: 0,
      isRunning: false,
      isPaused: false,
      showPresets: false,
      start: (seconds) =>
        set({ remaining: seconds, total: seconds, isRunning: true, isPaused: false, showPresets: false }),
      tick: () => {
        const { remaining } = get()
        if (remaining <= 1) {
          set({ remaining: 0, isRunning: false, isPaused: false })
        } else {
          set({ remaining: remaining - 1 })
        }
      },
      pause: () => set({ isPaused: true }),
      resume: () => set({ isPaused: false }),
      dismiss: () => set({ remaining: 0, total: 0, isRunning: false, isPaused: false, showPresets: false }),
      reset: () => set({ remaining: 0, total: 0, isRunning: false, isPaused: false, showPresets: true }),
    }),
    { name: "rest-timer-storage" }
  )
)
